import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    BlockStack,
    Box,
    Button,
    Card,
    FormLayout,
    IndexFilters,
    IndexTable,
    InlineStack,
    Layout,
    Page,
    RadioButton,
    Select,
    Spinner,
    Text,
    TextField,
    useBreakpoints,
    Icon,
    Banner,
    Link,
    DropZone,
    Pagination,
    useSetIndexFiltersMode,
} from '@shopify/polaris';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { SessionContext } from '#client/contexts/SessionContext';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { PERFORM_STATUSES, STATUS } from '#client/pages/CycleCounts/constants/cycleCount';
import { cycleCountService } from '#client/services/cycleCount/cycleCountService';
import { useBarcodeScanner } from '#client/utils/hooks/useBarcodeScanner';
import { hasPermissionForLocation } from '#client/utils/helpers';
import { InfoIcon, EditIcon, CheckIcon, XSmallIcon } from '@shopify/polaris-icons';
import styles from '#client/pages/CycleCounts/CycleCountPerform.module.css';
import ErrorModal from './components/ErrorModal';
import CycleCountBreadcrumbs from './CycleCountBreadcrumbs';
import NoPermissions from '#client/pages/NoPermissions';
import CycleCountDetails from '../components/CycleCountDetails';

// Constants
const ENTRY_MODES = {
    SCAN: 'scan',
    MANUAL: 'manual',
    CSV: 'csv',
};

const COUNTED_ITEMS_SORT_OPTIONS = [
    { label: 'Updated', directionLabel: 'Newest First', value: 'updatedAt desc' },
    { label: 'Updated', directionLabel: 'Oldest First', value: 'updatedAt asc' },
    { label: 'Product', directionLabel: 'A-Z', value: 'productName asc' },
    { label: 'Product', directionLabel: 'Z-A', value: 'productName desc' },
    { label: 'SKU', directionLabel: 'A-Z', value: 'sku asc' },
    { label: 'SKU', directionLabel: 'Z-A', value: 'sku desc' },
    { label: 'Counted Qty', directionLabel: 'Low to High', value: 'quantity asc' },
    { label: 'Counted Qty', directionLabel: 'High to Low', value: 'quantity desc' },
];

// Custom hooks
const useCycleCountData = (id, addToast) => {
    const [cycleCount, setCycleCount] = useState(null);
    const [batches, setBatches] = useState([]);
    const [currentBatch, setCurrentBatch] = useState(null);
    const [countedItems, setCountedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const fetchCycleCountDetails = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await cycleCountService.getPerformDetails(id);
            const data = response.data;

            if (data) {
                // if status is not in PERFORM_STATUSES, redirect to detail page
                if (!PERFORM_STATUSES.includes(data.status)) {
                    navigate(`/cycle-counts/${id}`, { replace: true });
                }

                setCycleCount(data);
                setBatches(data.batches || []);

                if (data.batches?.length > 0) {
                    setCurrentBatch(data.batches[0]);
                    setCountedItems([]);
                }
            }
        } catch (error) {
            console.error('Error fetching cycle count:', error);
            addToast({
                type: 'error',
                message: error.response?.data?.error?.message || 'Failed to load cycle count',
            });
        } finally {
            setIsLoading(false);
        }
    }, [id, addToast]);

    useEffect(() => {
        fetchCycleCountDetails();
    }, [fetchCycleCountDetails]);

    return {
        cycleCount,
        setCycleCount,
        batches,
        setBatches,
        currentBatch,
        setCurrentBatch,
        countedItems,
        setCountedItems,
        isLoading,
        fetchCycleCountDetails,
    };
};

// Subcomponents
const EntryModeSelector = ({ entryMode, onModeChange }) => (
    <InlineStack gap="200">
        <RadioButton label="Scan Mode" id="scan-mode" name="entry-mode" checked={entryMode === ENTRY_MODES.SCAN} onChange={() => onModeChange(ENTRY_MODES.SCAN)} />
        <RadioButton label="Manual Entry" id="manual-entry" name="entry-mode" checked={entryMode === ENTRY_MODES.MANUAL} onChange={() => onModeChange(ENTRY_MODES.MANUAL)} />
        <RadioButton label="CSV Upload" id="csv-upload" name="entry-mode" checked={entryMode === ENTRY_MODES.CSV} onChange={() => onModeChange(ENTRY_MODES.CSV)} />
    </InlineStack>
);

const ScanMode = () => (
    <Box padding="400" background="bg-surface-info" borderRadius="200" className={styles.infoBox}>
        <div className={styles.scanModeContainer}>
            <div className={styles.scanModeIcon}>
                <Icon source={InfoIcon} tone="info" />
            </div>
            <div className={styles.scanModeText}>
                <Text variant="bodyMd" as="p">
                    Start Scanning Items
                </Text>
            </div>
        </div>
    </Box>
);

const ManualEntryForm = ({ skuInput, setSkuInput, barcodeInput, setBarcodeInput, quantityInput, setQuantityInput, onSubmit }) => {
    const isValidSubmit = (skuInput.trim() || barcodeInput.trim()) && quantityInput.trim() && !isNaN(Number(quantityInput)) && Number(quantityInput) > 0;
    const isValidQtyuantity = quantityInput.trim() && !isNaN(Number(quantityInput)) && Number(quantityInput) > 0;

    return (
        <FormLayout>
            <Box style={{ paddingTop: '.5rem' }}>
                <FormLayout.Group>
                    <FormField onEnter={onSubmit}>
                        <TextField label="Product SKU" value={skuInput} onChange={setSkuInput} placeholder="Enter SKU" autoComplete="off" />
                    </FormField>
                    <FormField onEnter={onSubmit}>
                        <TextField label="Product Barcode" value={barcodeInput} onChange={setBarcodeInput} placeholder="Enter barcode" autoComplete="off" />
                    </FormField>
                </FormLayout.Group>
            </Box>
            <FormLayout.Group>
                <Box style={{ paddingTop: '.5rem' }}>
                    <FormField onEnter={onSubmit}>
                        <TextField label="Quantity" value={quantityInput} onChange={setQuantityInput} type="number" autoComplete="off" min="1" />
                        {!isValidQtyuantity && (
                            <div style={{ marginTop: '0.25rem' }}>
                                <Text variant="bodySm" tone="critical">
                                    Please enter a valid quantity
                                </Text>
                            </div>
                        )}
                    </FormField>
                </Box>
            </FormLayout.Group>
            <InlineStack align="end">
                <Button variant="primary" onClick={onSubmit} disabled={!isValidSubmit}>
                    Add Item
                </Button>
            </InlineStack>
        </FormLayout>
    );
};

const FormField = ({ onEnter, children }) => <div onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onEnter())}>{children}</div>;

const CsvUploadForm = ({ currentBatch, onUploadSuccess, addToast, cycleCountId, isUploading, setIsUploading }) => {
    const [file, setFile] = useState(null);
    const [csvPreview, setCsvPreview] = useState([]);

    const handleDownloadTemplate = useCallback(() => {
        // Create CSV content with headers and sample rows
        const headers = ['sku', 'quantity'];
        const sampleRows = [
            ['SKU-001', '10'],
            ['SKU-002', '5'],
            ['SKU-003', '15'],
        ];

        const csvContent = [headers.join(','), ...sampleRows.map((row) => row.join(','))].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', 'cycle-count-template.csv');
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    const handleDropZoneUpload = useCallback(
        async (files) => {
            if (files.length > 0) {
                const droppedFile = files[0];
                if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
                    setFile(droppedFile);

                    // Parse and preview first 10 rows
                    try {
                        const text = await droppedFile.text();
                        const lines = text.split('\n').filter((line) => line.trim());

                        if (lines.length < 2) {
                            setCsvPreview([]);
                            return;
                        }

                        const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
                        const skuIndex = header.findIndex((h) => h === 'sku');
                        const quantityIndex = header.findIndex((h) => h === 'quantity' || h === 'qty');

                        if (skuIndex === -1 || quantityIndex === -1) {
                            setCsvPreview([]);
                            return;
                        }

                        const preview = [];
                        const previewLimit = Math.min(11, lines.length); // 1 header + 10 data rows

                        for (let i = 1; i < previewLimit; i++) {
                            const values = lines[i].split(',').map((v) => v.trim());
                            const item = {
                                sku: values[skuIndex],
                                quantity: values[quantityIndex],
                            };
                            preview.push(item);
                        }

                        setCsvPreview(preview);
                    } catch (error) {
                        console.error('Error parsing CSV preview:', error);
                        setCsvPreview([]);
                    }
                } else {
                    addToast({ type: 'error', message: 'Please upload a CSV file' });
                }
            }
        },
        [addToast]
    );

    const handleSubmit = useCallback(async () => {
        if (!file) {
            addToast({ type: 'error', message: 'Please select a CSV file first' });
            return;
        }

        if (!currentBatch) {
            addToast({ type: 'error', message: 'Please select a batch first' });
            return;
        }

        setIsUploading(true);

        try {
            // The server wraps its payload in `data`; reading the top level only ever
            // found the fallback text, so the summary and row errors never showed.
            const { message, results } = (await cycleCountService.uploadCsvToBatch(cycleCountId, currentBatch.id, file)).data || {};

            addToast({
                type: 'success',
                message: message || 'CSV uploaded successfully',
            });

            // Show errors if any
            if (results && results.errors && results.errors.length > 0) {
                console.warn('CSV upload errors:', results.errors);
                if (results.errors.length <= 5) {
                    // Show first few errors
                    results.errors.forEach((error) => {
                        addToast({ type: 'error', message: error });
                    });
                } else {
                    addToast({
                        type: 'warning',
                        message: `${results.failed} items failed to process. Check console for details.`,
                    });
                }
            }

            setFile(null);
            setCsvPreview([]);

            // Callback to refresh the counted items
            if (onUploadSuccess) {
                onUploadSuccess();
            }
        } catch (error) {
            console.error('CSV upload error:', error);
            addToast({
                type: 'error',
                message: error.response?.data?.message || error.message || 'Failed to process CSV file',
            });
        } finally {
            setIsUploading(false);
        }
    }, [file, currentBatch, cycleCountId, addToast, onUploadSuccess, setIsUploading]);

    const handleRemoveFile = useCallback(() => {
        setFile(null);
        setCsvPreview([]);
    }, []);

    return (
        <BlockStack gap="400">
            <Box paddingBlockEnd="200">
                <Text variant="bodyMd" tone="subdued">
                    Upload a CSV file with columns: <strong>sku</strong> and <strong>quantity</strong>
                </Text>
            </Box>

            <DropZone accept="text/csv,.csv,application/vnd.ms-excel,application/x-csv,application/csv,text/x-csv" type="file" allowMultiple={false} onDrop={handleDropZoneUpload}>
                {!file ? (
                    <DropZone.FileUpload actionTitle="Add CSV file" />
                ) : (
                    <Box padding="400">
                        <BlockStack gap="200" align="center">
                            <Text variant="bodyMd" fontWeight="semibold">
                                {file.name}
                            </Text>
                            <Text variant="bodySm" tone="subdued">
                                {(file.size / 1024).toFixed(2)} KB
                            </Text>
                        </BlockStack>
                    </Box>
                )}
            </DropZone>

            {csvPreview.length > 0 && (
                <BlockStack gap="300">
                    <Text variant="headingSm" as="h3">
                        CSV Output (First 10 items)
                    </Text>
                    <IndexTable itemCount={csvPreview.length} headings={[{ title: 'SKU' }, { title: 'Quantity' }]} selectable={false}>
                        {csvPreview.map((item, index) => (
                            <IndexTable.Row key={index} position={index}>
                                <IndexTable.Cell>
                                    <Text variant="bodyMd">{item.sku}</Text>
                                </IndexTable.Cell>
                                <IndexTable.Cell>
                                    <Text variant="bodyMd">{item.quantity}</Text>
                                </IndexTable.Cell>
                            </IndexTable.Row>
                        ))}
                    </IndexTable>
                </BlockStack>
            )}

            {isUploading && (
                <BlockStack padding="400">
                    <Banner tone="info">
                        <BlockStack gap="200">
                            <Text variant="bodyMd" fontWeight="semibold">
                                Uploading and processing CSV...
                            </Text>
                            <Text variant="bodySm">This may take a few minutes as we process the CSV.</Text>
                        </BlockStack>
                    </Banner>
                </BlockStack>
            )}
            <Box paddingBlockEnd="200">
                <Button variant="secondary" onClick={handleDownloadTemplate}>
                    Download CSV Template
                </Button>
            </Box>
            {file && (
                <InlineStack gap="200" align="end">
                    {csvPreview.length === 0 && (
                        <Banner tone="critical">
                            <Text variant="bodySm">{'CSV preview unavailable. Please ensure the file has "sku" and "quantity" columns.'}</Text>
                        </Banner>
                    )}
                    <Button onClick={handleRemoveFile} disabled={isUploading || csvPreview.length === 0}>
                        Remove
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} loading={isUploading} disabled={!currentBatch || isUploading || csvPreview.length === 0}>
                        Upload & Process
                    </Button>
                </InlineStack>
            )}

            {!currentBatch && (
                <Banner tone="warning">
                    <Text variant="bodySm">Please select a batch before uploading CSV</Text>
                </Banner>
            )}
        </BlockStack>
    );
};

const BatchManager = ({
    batches,
    currentBatch,
    showBatchCreate,
    newBatchDescription,
    editingBatchId,
    editBatchDescription,
    onToggleCreate,
    onCreateBatch,
    onBatchSelect,
    onStartEditBatch,
    onCancelEditBatch,
    onUpdateBatchDescription,
    setNewBatchDescription,
    setEditBatchDescription,
}) => {
    const batchOptions = useMemo(
        () =>
            batches.map((batch) => ({
                label: batch.description || 'Unnamed Batch',
                value: batch.id.toString(),
            })),
        [batches]
    );

    return (
        <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingLg">
                    Batches
                </Text>
                <Button variant="primary" onClick={onToggleCreate}>
                    {showBatchCreate ? 'Cancel' : 'Create Batch'}
                </Button>
            </InlineStack>

            <div style={{ width: '100%', maxWidth: '600px' }}>
                {showBatchCreate && (
                    <div style={{ marginBottom: '16px' }}>
                        <Card>
                            <BlockStack gap="300">
                                <TextField label="Batch Description" value={newBatchDescription} onChange={setNewBatchDescription} placeholder="Enter batch description" autoComplete="off" />
                                <InlineStack gap="200">
                                    <Button onClick={onToggleCreate}>Cancel</Button>
                                    <Button variant="primary" onClick={onCreateBatch}>
                                        Create
                                    </Button>
                                </InlineStack>
                            </BlockStack>
                        </Card>
                    </div>
                )}

                {batches.length > 0 && (
                    <>
                        <Select label="Current Batch" options={batchOptions} value={currentBatch?.id.toString()} onChange={onBatchSelect} />

                        {currentBatch && (
                            <Box padding="300" background="bg-surface-selected" borderRadius="200" paddingBlockStart="400">
                                {editingBatchId === currentBatch.id ? (
                                    <BlockStack gap="200">
                                        <TextField value={editBatchDescription} onChange={setEditBatchDescription} placeholder="Enter batch description" autoComplete="off" />
                                        <InlineStack gap="200">
                                            <Button size="slim" onClick={onCancelEditBatch}>
                                                Cancel
                                            </Button>
                                            <Button size="slim" variant="primary" onClick={onUpdateBatchDescription}>
                                                Save
                                            </Button>
                                        </InlineStack>
                                    </BlockStack>
                                ) : (
                                    <InlineStack align="space-between" blockAlign="center">
                                        <Text variant="bodyMd" fontWeight="semibold">
                                            {currentBatch.description || 'Unnamed Batch'}
                                        </Text>
                                        <Button size="slim" onClick={() => onStartEditBatch(currentBatch)}>
                                            Edit
                                        </Button>
                                    </InlineStack>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </div>
        </BlockStack>
    );
};

const ExpectedItemsCard = ({ cycleCountId, breakpoints }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('sku');
    const [sortDirection, setSortDirection] = useState('asc');
    const pageSize = 12;

    // Fetch expected items from starting inventory
    const { data: itemsData, isFetching } = useQuery({
        queryKey: ['expectedItems', cycleCountId, currentPage, sortBy, sortDirection],
        queryFn: () => cycleCountService.getItems(cycleCountId, currentPage, pageSize, sortBy, sortDirection),
        enabled: !!cycleCountId,
        placeholderData: (previousData) => previousData,
    });

    const items = itemsData?.data?.items || [];
    const pagination = itemsData?.data?.pagination || null;

    // Sort handler
    const handleSort = useCallback((headingIndex, direction) => {
        const fields = ['productName', 'variant', 'sku'];
        setSortBy(fields[headingIndex]);
        setSortDirection(direction === 'descending' ? 'desc' : 'asc');
        setCurrentPage(1);
    }, []);

    const getSortColumnIndex = () => {
        const fields = ['productName', 'variant', 'sku'];
        return fields.indexOf(sortBy);
    };

    const getSortDirection = () => {
        return sortDirection === 'desc' ? 'descending' : 'ascending';
    };

    return (
        <Card>
            <BlockStack gap="400">
                <InlineStack align="space-between">
                    <Text variant="headingLg">Expected Items</Text>
                    {pagination && (
                        <Text variant="bodyMd" tone="subdued">
                            {pagination.totalCount} items to count
                        </Text>
                    )}
                </InlineStack>

                <IndexTable
                    condensed={breakpoints.smDown}
                    itemCount={items.length}
                    sortable={[true, true, true]}
                    sortDirection={getSortDirection()}
                    sortColumnIndex={getSortColumnIndex()}
                    onSort={handleSort}
                    headings={[{ title: 'Product' }, { title: 'Variant' }, { title: 'SKU' }]}
                    selectable={false}
                >
                    {items.map((item, index) => (
                        <IndexTable.Row key={`${item.sku}-${index}`} id={`${item.sku}-${index}`} position={index}>
                            <IndexTable.Cell>
                                <InlineStack gap="300" blockAlign="center">
                                    {item.product?.image && (
                                        <div style={{ width: '40px', height: '40px', flexShrink: 0 }}>
                                            <img
                                                src={item.product.image}
                                                alt={item.product?.title || 'Product'}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    borderRadius: '4px',
                                                }}
                                            />
                                        </div>
                                    )}
                                    <Text variant="bodyMd">{item.product?.title || 'Unknown Product'}</Text>
                                </InlineStack>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                                <Text variant="bodyMd">{item.title || 'N/A'}</Text>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                                <Text variant="bodyMd">{item.sku}</Text>
                            </IndexTable.Cell>
                        </IndexTable.Row>
                    ))}
                </IndexTable>

                {pagination && (
                    <Box padding="400">
                        <InlineStack align="left" gap="200">
                            <Pagination
                                hasPrevious={pagination.hasPrevious && !isFetching}
                                hasNext={pagination.hasNext && !isFetching}
                                onPrevious={() => setCurrentPage(currentPage - 1)}
                                onNext={() => setCurrentPage(currentPage + 1)}
                                label={`Page ${currentPage} of ${pagination.totalPages}`}
                            />
                        </InlineStack>
                    </Box>
                )}
            </BlockStack>
        </Card>
    );
};

const CountedItemsTable = ({
    countedItems,
    editingItemSku,
    editingQty,
    onEditClick,
    onQtyChange,
    onSaveEdit,
    onCancelEdit,
    onDeleteSku,
    breakpoints,
    lastAddedSku,
    currentPage,
    totalPages,
    onPageChange,
    isLoadingItems,
    queryValue,
    onQueryChange,
    onQueryClear,
    sortSelected,
    onSortChange,
    exactSkuFilter,
    onExactSkuFilterChange,
    onClearSkuFilter,
    quantityMinFilter,
    quantityMaxFilter,
    onQuantityMinFilterChange,
    onQuantityMaxFilterChange,
    onClearQuantityRange,
}) => {
    const { mode, setMode } = useSetIndexFiltersMode('FILTERING');

    const getProductTitle = useCallback((item) => {
        return item.product?.title || item.title || '';
    }, []);

    const filters = useMemo(
        () => [
            {
                key: 'sku',
                label: 'Exact SKU',
                filter: <TextField label="Exact SKU" labelHidden value={exactSkuFilter} onChange={onExactSkuFilterChange} autoComplete="off" placeholder="Enter SKU" />,
                pinned: true,
            },
            {
                key: 'quantityRange',
                label: 'Quantity range',
                filter: (
                    <InlineStack gap="200">
                        <div style={{ minWidth: '120px' }}>
                            <TextField label="Min" labelHidden type="number" value={quantityMinFilter} onChange={onQuantityMinFilterChange} autoComplete="off" placeholder="Min" />
                        </div>
                        <div style={{ minWidth: '120px' }}>
                            <TextField label="Max" labelHidden type="number" value={quantityMaxFilter} onChange={onQuantityMaxFilterChange} autoComplete="off" placeholder="Max" />
                        </div>
                    </InlineStack>
                ),
            },
        ],
        [exactSkuFilter, onExactSkuFilterChange, quantityMinFilter, quantityMaxFilter, onQuantityMinFilterChange, onQuantityMaxFilterChange]
    );

    const appliedFilters = useMemo(() => {
        const result = [];

        if (exactSkuFilter) {
            result.push({
                key: 'sku',
                label: `SKU: ${exactSkuFilter}`,
                onRemove: onClearSkuFilter,
            });
        }

        if (quantityMinFilter || quantityMaxFilter) {
            const min = quantityMinFilter || 'Any';
            const max = quantityMaxFilter || 'Any';
            result.push({
                key: 'quantityRange',
                label: `Quantity: ${min} - ${max}`,
                onRemove: onClearQuantityRange,
            });
        }

        return result;
    }, [exactSkuFilter, quantityMinFilter, quantityMaxFilter, onClearSkuFilter, onClearQuantityRange]);

    const handleClearAll = useCallback(() => {
        onQueryClear();
        onClearSkuFilter();
        onClearQuantityRange();
    }, [onQueryClear, onClearSkuFilter, onClearQuantityRange]);

    const renderEditingControls = (item) => {
        const id = item.id;
        const editingValue = editingQty[id];
        const isValidQty = String(editingValue).trim() !== '' && !isNaN(Number(editingValue)) && Number(editingValue) > 0;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), isValidQty && onSaveEdit(id))}>
                    <TextField value={editingValue} onChange={(val) => onQtyChange(val, id)} autoFocus type="text" labelHidden style={{ width: 60 }} />
                    {!isValidQty && (
                        <div style={{ marginTop: '0.25rem' }}>
                            <Text variant="bodySm" tone="critical">
                                Please enter a valid quantity
                            </Text>
                        </div>
                    )}
                </div>
                <Button icon={CheckIcon} variant="monochromePlain" onClick={() => onSaveEdit(id)} accessibilityLabel="Save" disabled={!isValidQty} />
                <Button icon={XSmallIcon} variant="monochromePlain" onClick={onCancelEdit} accessibilityLabel="Cancel" />
            </div>
        );
    };

    return (
        <BlockStack gap="300">
            <IndexFilters
                mode={mode}
                setMode={setMode}
                sortOptions={COUNTED_ITEMS_SORT_OPTIONS}
                sortSelected={sortSelected}
                onSort={onSortChange}
                queryValue={queryValue}
                queryPlaceholder="Search Counted Items"
                onQueryChange={onQueryChange}
                onQueryClear={onQueryClear}
                onClearAll={handleClearAll}
                filters={filters}
                appliedFilters={appliedFilters}
                tabs={[]}
                autoFocusSearchField={false}
            />
            {countedItems.length === 0 ? (
                <Box padding="400">
                    <Text variant="bodyMd" tone="subdued" alignment="center">
                        {isLoadingItems ? 'Loading items...' : appliedFilters.length > 0 || queryValue !== '' ? 'No items found for current filters' : 'No items counted yet'}
                    </Text>
                </Box>
            ) : (
                <>
                    <div className={styles.indexTableWrapper}>
                        <IndexTable
                            condensed={breakpoints.smDown}
                            resourceName={{ singular: 'item', plural: 'items' }}
                            itemCount={countedItems.length}
                            headings={[{ title: 'Product' }, { title: 'SKU' }, { title: 'Counted Qty' }, { title: 'Actions' }]}
                            selectable={false}
                            stickyHeader
                        >
                            {countedItems.map((item, index) => {
                                const isEditing = editingItemSku === item.id;
                                const isLastAdded = lastAddedSku === item.sku;

                                return (
                                    <IndexTable.Row id={item.id + '-' + index} key={item.sku + '-' + index} position={index} selected={isLastAdded ? true : false}>
                                        <IndexTable.Cell>
                                            <Text variant="bodyMd" fontWeight="semibold">
                                                {getProductTitle(item)}
                                            </Text>
                                        </IndexTable.Cell>
                                        <IndexTable.Cell>
                                            <Text variant="bodyMd">{item.sku}</Text>
                                        </IndexTable.Cell>
                                        <IndexTable.Cell>{isEditing ? renderEditingControls(item) : <Text variant="bodyMd">{item.quantity}</Text>}</IndexTable.Cell>
                                        <IndexTable.Cell>
                                            <Button icon={EditIcon} variant="plain" onClick={() => onEditClick(item.id, item.quantity)} disabled={isEditing} />
                                            <Button icon={XSmallIcon} variant="plain" onClick={() => onDeleteSku(item.id)} disabled={isEditing} />
                                        </IndexTable.Cell>
                                    </IndexTable.Row>
                                );
                            })}
                        </IndexTable>
                    </div>

                    <Box padding="400">
                        <InlineStack align="left" gap="200">
                            <Pagination
                                hasPrevious={currentPage > 1 && !isLoadingItems}
                                hasNext={currentPage < totalPages && !isLoadingItems}
                                onPrevious={() => onPageChange(currentPage - 1)}
                                onNext={() => onPageChange(currentPage + 1)}
                                label={`Page ${currentPage} of ${totalPages}`}
                            />
                        </InlineStack>
                    </Box>
                </>
            )}
        </BlockStack>
    );
};

// Main Component
const CycleCountPerform = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToast, clearToasts } = useToast();
    const { currentUser } = useContext(SessionContext);
    const breakpoints = useBreakpoints();
    const queryClient = useQueryClient();

    const { cycleCount, setCycleCount, batches, setBatches, currentBatch, setCurrentBatch, countedItems, setCountedItems, isLoading } = useCycleCountData(id, addToast);

    // Entry mode state
    const [entryMode, setEntryMode] = useState(ENTRY_MODES.SCAN);
    const [skuInput, setSkuInput] = useState('');
    const [barcodeInput, setBarcodeInput] = useState('');
    const [quantityInput, setQuantityInput] = useState('1');
    const [isCsvUploading, setIsCsvUploading] = useState(false);

    // Batch state
    const [showBatchCreate, setShowBatchCreate] = useState(false);
    const [newBatchDescription, setNewBatchDescription] = useState('');
    const [editingBatchId, setEditingBatchId] = useState(null);
    const [editBatchDescription, setEditBatchDescription] = useState('');

    // Item editing state - use SKU instead of index
    const [editingItemSku, setEditingItemSku] = useState(null);
    const [editingQty, setEditingQty] = useState({});
    const [lastAddedSku, setLastAddedSku] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const pageSize = 12;

    // Counted items filter/sort state
    const [countedItemsQuery, setCountedItemsQuery] = useState('');
    const [countedItemsSortSelected, setCountedItemsSortSelected] = useState(['updatedAt desc']);
    const [countedItemsSkuFilter, setCountedItemsSkuFilter] = useState('');
    const [countedItemsQuantityMin, setCountedItemsQuantityMin] = useState('');
    const [countedItemsQuantityMax, setCountedItemsQuantityMax] = useState('');

    // Error modal state
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [statusErrors, setStatusErrors] = useState([]);

    // Status polling - poll every 5 seconds when status is PREPARING
    const { data: statusData, refetch: refetchStatus } = useQuery({
        queryKey: ['cycleCountStatus', id],
        queryFn: async () => {
            const response = await cycleCountService.getStatus(id);
            return response.data;
        },
        refetchInterval: (query) => {
            const data = query?.state?.data;
            const status = data?.status || cycleCount?.status;
            const type = data?.type || cycleCount?.type;
            // Poll every 30 seconds when PREPARING, stop otherwise
            if (status === 'PREPARING') {
                if (type === 'FULL') {
                    return 30000; // 30 seconds for FULL counts
                } else {
                    return 5000; // 5 seconds for PARTIAL counts
                }
            } else {
                return false;
            }
        },
        refetchIntervalInBackground: true,
        enabled: !!id && !!cycleCount,
    });

    // Handle status changes from polling
    useEffect(() => {
        if (!statusData) return;

        const newStatus = statusData.status;
        const errors = statusData.countErrors || [];

        // Update local cycle count status
        if (cycleCount && cycleCount.status !== newStatus) {
            setCycleCount({ ...cycleCount, status: newStatus });
        }

        // Handle ERROR status
        if (newStatus === 'ERROR' && errors.length > 0) {
            setStatusErrors(errors);
        }

        // Handle transition from PREPARING to IN_PROGRESS
        if (cycleCount?.status === 'PREPARING') {
            // Refetch full cycle count data to get batches
            refetchStatus();
        }

        if (newStatus === 'IN_PROGRESS') {
            clearToasts();
            addToast({
                type: 'success',
                message: 'Cycle count is ready. You can now begin counting.',
            });
        }
    }, [statusData, cycleCount, setCycleCount, refetchStatus, addToast]);

    // Invalidate cycle-counts cache after creating a new count
    const invalidateCycleCountsCache = useCallback(() => {
        queryClient.invalidateQueries(['cycle-counts']);
    }, []);

    const countedItemsQueryParams = useMemo(() => {
        const sortValue = countedItemsSortSelected[0] || 'updatedAt desc';
        const [sortBy = 'updatedAt', sortDirection = 'desc'] = sortValue.split(' ');

        return {
            sortBy,
            sortDirection,
            query: countedItemsQuery.trim() || undefined,
            sku: countedItemsSkuFilter.trim() || undefined,
            quantityMin: countedItemsQuantityMin !== '' ? Number(countedItemsQuantityMin) : undefined,
            quantityMax: countedItemsQuantityMax !== '' ? Number(countedItemsQuantityMax) : undefined,
        };
    }, [countedItemsSortSelected, countedItemsQuery, countedItemsSkuFilter, countedItemsQuantityMin, countedItemsQuantityMax]);

    // Batch handlers
    const fetchBatchItems = useCallback(
        async (batchId, page = 1) => {
            setIsLoadingItems(true);
            try {
                const loadPage = async (targetPage) => {
                    const response = await cycleCountService.getBatchItems(id, batchId, targetPage, pageSize, countedItemsQueryParams);

                    if (!response.success) {
                        return;
                    }

                    const fetchedItems = response.data.items;
                    const pagination = response.data.pagination;

                    if (fetchedItems.length === 0 && targetPage > 1 && pagination.totalPages > 0 && targetPage > pagination.totalPages) {
                        await loadPage(pagination.totalPages);
                        return;
                    }

                    setCountedItems(fetchedItems);
                    setCurrentPage(pagination.page);
                    setTotalPages(pagination.totalPages);
                };

                await loadPage(page);
            } catch (error) {
                console.error('Error fetching batch items:', error);
                addToast({
                    type: 'error',
                    message: 'Failed to load batch items',
                });
            } finally {
                setIsLoadingItems(false);
            }
        },
        [id, pageSize, countedItemsQueryParams, addToast, setCountedItems]
    );

    useEffect(() => {
        if (currentBatch) {
            fetchBatchItems(currentBatch.id, currentPage);
        }
    }, [currentBatch, currentPage, countedItemsQueryParams, fetchBatchItems]);

    const refreshCurrentBatchItems = useCallback(async () => {
        if (currentBatch) {
            await fetchBatchItems(currentBatch.id, currentPage);
        }
    }, [currentBatch, currentPage, fetchBatchItems]);

    const handleBatchSelect = useCallback(
        (batchId) => {
            const batch = batches.find((b) => b.id === parseInt(batchId));
            if (batch) {
                setCurrentBatch(batch);
                setCurrentPage(1);
            }
        },
        [batches]
    );

    const handleCreateBatch = useCallback(async () => {
        if (!newBatchDescription.trim()) {
            addToast({ type: 'error', message: 'Batch description is required' });
            return;
        }

        try {
            const response = await cycleCountService.createBatch(id, {
                description: newBatchDescription.trim(),
            });

            if (response.success) {
                const newBatch = response.data.batch;
                setBatches([...batches, newBatch]);
                setCurrentBatch(newBatch);
                setCountedItems([]);
                setCurrentPage(1);
                setTotalPages(1);
                setNewBatchDescription('');
                setShowBatchCreate(false);
                addToast({ type: 'success', message: 'Batch created successfully' });
            }
        } catch (error) {
            addToast({
                type: 'error',
                message: error.response?.data?.error?.message || 'Failed to create batch',
            });
        }
    }, [id, batches, newBatchDescription, addToast]);

    const handleUpdateBatchDescription = useCallback(async () => {
        if (!editBatchDescription.trim()) {
            addToast({ type: 'error', message: 'Batch description is required' });
            return;
        }

        try {
            const response = await cycleCountService.updateBatchDescription(id, editingBatchId, editBatchDescription.trim());

            if (response.success) {
                setBatches(batches.map((b) => (b.id === editingBatchId ? { ...b, description: editBatchDescription.trim() } : b)));

                if (currentBatch?.id === editingBatchId) {
                    setCurrentBatch({ ...currentBatch, description: editBatchDescription.trim() });
                }

                setEditingBatchId(null);
                setEditBatchDescription('');
                addToast({ type: 'success', message: 'Batch description updated successfully' });
            }
        } catch (error) {
            addToast({
                type: 'error',
                message: error.response?.data?.error?.message || 'Failed to update batch description',
            });
        }
    }, [id, editingBatchId, editBatchDescription, batches, currentBatch, addToast]);

    const handleManualSubmit = useCallback(
        async (e) => {
            if (e) e.preventDefault();

            if (!skuInput.trim() && !barcodeInput.trim()) {
                addToast({ type: 'error', message: 'Product SKU or Barcode is required' });
                return;
            }

            if (!quantityInput.trim() || isNaN(Number(quantityInput)) || Number(quantityInput) <= 0) {
                addToast({ type: 'error', message: 'Please enter a valid quantity' });
                return;
            }

            if (!currentBatch) {
                addToast({ type: 'error', message: 'Please select a batch first' });
                return;
            }

            try {
                clearToasts();

                const isSku = Boolean(skuInput.trim());
                const isBarcode = Boolean(barcodeInput.trim());
                let payloadItem = {
                    countedQuantity: Number(quantityInput),
                };

                if (isSku) {
                    payloadItem.sku = skuInput.trim();
                } else if (isBarcode) {
                    payloadItem.barcode = barcodeInput.trim();
                }

                const response = await cycleCountService.updateBatch(id, currentBatch.id, {
                    item: payloadItem,
                    action: 'increment',
                });

                if (!response.success) {
                    addToast({ type: 'error', message: response.error?.message || 'Failed to process barcode', playSound: true });
                    return;
                }

                const updatedItems = response.data.variants;
                if (updatedItems.length == 0) {
                    addToast({ type: 'error', message: 'No variant data returned from server', playSound: true });
                    return;
                }

                updatedItems.map((item) => {
                    const productTitle = `${item.product?.title}  (${item.title || ''})`;
                    setLastAddedSku(item.sku);
                    setTimeout(() => setLastAddedSku(null), 760);
                    addToast({ type: 'success', message: <div style={{ maxWidth: '400px' }}>Successfully added {productTitle}</div> });
                });

                await refreshCurrentBatchItems();

                setSkuInput('');
                setBarcodeInput('');
                setQuantityInput('1');
            } catch (error) {
                addToast({
                    type: 'error',
                    message: error.response?.data?.error?.message || 'Failed to process barcode',
                    playSound: true,
                });
            }
        },
        [skuInput, barcodeInput, quantityInput, currentBatch, id, addToast, clearToasts, refreshCurrentBatchItems]
    );

    const handleBarcodeScanned = useCallback(
        async (barcode) => {
            if (!barcode || !currentBatch) return;

            try {
                clearToasts();

                const response = await cycleCountService.updateBatch(id, currentBatch.id, {
                    item: { barcode, countedQuantity: 1 },
                    action: 'increment',
                });

                if (!response.success) {
                    addToast({ type: 'error', message: response.error?.message || 'Failed to process scanned barcode', playSound: true });
                    return;
                }

                const updatedItems = response.data.variants;
                if (updatedItems.length == 0) {
                    addToast({ type: 'error', message: 'No variant data returned from server for scanned barcode', playSound: true });
                    return;
                }

                updatedItems.map((updatedItem) => {
                    const productTitle = `${updatedItem.product?.title}  (${updatedItem.title || ''})`;
                    setLastAddedSku(updatedItem.sku);
                    setTimeout(() => setLastAddedSku(null), 760);
                    addToast({ type: 'success', message: <div style={{ maxWidth: '400px' }}>Successfully added {productTitle}</div> });
                });

                await refreshCurrentBatchItems();
            } catch (error) {
                addToast({
                    type: 'error',
                    message: `Failed to process barcode: ${barcode}`,
                    playSound: true,
                });
            }
        },
        [currentBatch, id, addToast, clearToasts, refreshCurrentBatchItems]
    );

    const { startScanning, stopScanning } = useBarcodeScanner({ handleCallback: handleBarcodeScanned });

    const handleMode = useCallback(
        (mode) => {
            setEntryMode(mode);
            mode === ENTRY_MODES.SCAN ? startScanning() : stopScanning();
        },
        [startScanning, stopScanning]
    );

    useEffect(() => {
        if (entryMode === ENTRY_MODES.SCAN) {
            startScanning();
        }
    }, [entryMode, startScanning]);

    const handleStartCount = useCallback(async () => {
        try {
            // Invalidate cycle-counts list to reflect status change
            invalidateCycleCountsCache();

            const response = await cycleCountService.startCount(id);
            if (response.success) {
                setCycleCount((prev) => ({ ...prev, status: STATUS.PREPARING }));
                addToast({ type: 'success', message: 'Cycle count is preparing. Please wait.' });
            }
        } catch (error) {
            addToast({
                type: 'error',
                message: error.response?.data?.error?.message || 'Failed to start cycle count',
            });
        }
    }, [id, addToast]);

    const handleSubmitCount = useCallback(async () => {
        try {
            const response = await cycleCountService.submitForApproval(id);
            if (response.success) {
                addToast({ type: 'success', message: 'Cycle count submitted for approval' });
                queryClient.invalidateQueries(['cycleCount', id]);
                navigate(`/cycle-counts/${id}`);
            }
        } catch (error) {
            addToast({
                type: 'error',
                message: error.response?.data?.error?.message || 'Failed to submit cycle count',
            });
        }
    }, [id, addToast, navigate, queryClient]);

    // Item editing handlers
    const handleEditClick = useCallback((id, currentQty) => {
        setEditingItemSku(id);
        setEditingQty({ [id]: currentQty });
    }, []);

    const handleQtyChange = useCallback((value, id) => {
        setEditingQty((prev) => ({ ...prev, [id]: value }));
    }, []);

    const handleCancelEdit = useCallback(() => {
        setEditingItemSku(null);
        setEditingQty({});
    }, []);

    const handleSaveEdit = useCallback(
        async (vid) => {
            const item = countedItems.find((item) => item.id === vid);
            const newQty = editingQty[vid];

            if (!newQty || isNaN(Number(newQty)) || Number(newQty) <= 0) {
                addToast({ type: 'error', message: 'Please enter a valid quantity' });
                return;
            }

            try {
                const response = await cycleCountService.updateBatch(id, currentBatch.id, {
                    item: { sku: item.sku, countedQuantity: Number(newQty), variantId: item.variantId },
                });

                if (!response.success) {
                    addToast({ type: 'error', message: 'Failed to update quantity' });
                    return;
                }

                await refreshCurrentBatchItems();

                setEditingItemSku(null);
                setEditingQty({});
                addToast({ type: 'success', message: 'Quantity updated successfully' });
            } catch (error) {
                addToast({
                    type: 'error',
                    message: error.response?.data?.error?.message || 'Failed to update quantity',
                });
            }
        },
        [countedItems, editingQty, id, currentBatch, addToast, refreshCurrentBatchItems]
    );

    const handleCsvUploadSuccess = useCallback(async () => {
        // Refetch the current batch items to include the newly uploaded items
        if (currentBatch) {
            await refreshCurrentBatchItems();
            addToast({ type: 'info', message: 'Refreshing counted items...' });
        }
    }, [currentBatch, refreshCurrentBatchItems, addToast]);

    const handlePageChange = useCallback(
        (newPage) => {
            if (newPage >= 1 && newPage <= totalPages) {
                setCurrentPage(newPage);
            }
        },
        [totalPages]
    );

    // Delete sku from counted items
    const handleDeleteSku = useCallback(
        async (vid) => {
            if (!currentBatch) return;

            const item = countedItems.find((item) => item.id === vid);

            try {
                const response = await cycleCountService.updateBatch(id, currentBatch.id, {
                    item: { sku: item.sku, countedQuantity: 0, variantId: item.variantId },
                });

                if (!response.success) {
                    addToast({ type: 'error', message: 'Failed to delete item' });
                    return;
                }

                await refreshCurrentBatchItems();
                addToast({ type: 'success', message: 'Item deleted successfully' });
            } catch (error) {
                addToast({
                    type: 'error',
                    message: error.response?.data?.error?.message || 'Failed to delete item',
                });
            }
        },
        [id, currentBatch, addToast, refreshCurrentBatchItems, countedItems]
    );

    const handleCountedItemsQueryChange = useCallback((value) => {
        setCountedItemsQuery(value);
        setCurrentPage(1);
    }, []);

    const handleCountedItemsQueryClear = useCallback(() => {
        setCountedItemsQuery('');
        setCurrentPage(1);
    }, []);

    const handleCountedItemsSortChange = useCallback((value) => {
        if (value && Array.isArray(value) && value.length > 0) {
            setCountedItemsSortSelected(value);
            setCurrentPage(1);
        }
    }, []);

    const handleCountedItemsSkuFilterChange = useCallback((value) => {
        setCountedItemsSkuFilter(value);
        setCurrentPage(1);
    }, []);

    const handleCountedItemsSkuFilterClear = useCallback(() => {
        setCountedItemsSkuFilter('');
        setCurrentPage(1);
    }, []);

    const handleCountedItemsQuantityMinChange = useCallback((value) => {
        setCountedItemsQuantityMin(value);
        setCurrentPage(1);
    }, []);

    const handleCountedItemsQuantityMaxChange = useCallback((value) => {
        setCountedItemsQuantityMax(value);
        setCurrentPage(1);
    }, []);

    const handleCountedItemsQuantityRangeClear = useCallback(() => {
        setCountedItemsQuantityMin('');
        setCountedItemsQuantityMax('');
        setCurrentPage(1);
    }, []);

    // Permission check
    if (currentUser && cycleCount && !hasPermissionForLocation(currentUser, cycleCount?.locationId)) {
        return <NoPermissions permission="cycle-count" />;
    }

    // Loading state
    if (isLoading) {
        return (
            <>
                <CycleCountBreadcrumbs cycleCount={null} isLoading={true} />

                <Page fullWidth>
                    <Layout>
                        <Layout.Section>
                            <Card>
                                <Box padding="400" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                                    <Spinner size="large" />
                                </Box>
                            </Card>
                        </Layout.Section>
                    </Layout>
                </Page>
            </>
        );
    }

    if (!cycleCount) {
        return <NoPermissions permission="cycle-count" />;
    }

    return (
        <>
            <CycleCountBreadcrumbs cycleCount={cycleCount} isLoading={isLoading} />

            <Page fullWidth>
                <Layout>
                    <Layout.Section>
                        <CycleCountDetails cycleCount={cycleCount} onStartCount={handleStartCount} onSubmitCount={handleSubmitCount} isSubmitDisabled={isCsvUploading} />
                    </Layout.Section>

                    {cycleCount.status === 'PREPARING' && (
                        <Layout.Section>
                            <Banner tone="info">
                                <BlockStack gap="200">
                                    <Text variant="bodyMd" fontWeight="semibold">
                                        Cycle count is preparing, please wait...
                                    </Text>
                                    <Text variant="bodySm">This could take a few minutes while we fetch inventory data.</Text>
                                </BlockStack>
                            </Banner>
                        </Layout.Section>
                    )}

                    {cycleCount.status === 'ERROR' && (
                        <Layout.Section>
                            <Banner tone="critical">
                                <BlockStack gap="200">
                                    <Text variant="bodyMd" fontWeight="semibold">
                                        An error occurred while preparing the cycle count
                                    </Text>
                                    <Link removeUnderline onClick={() => setShowErrorModal(true)}>
                                        Show Errors
                                    </Link>
                                </BlockStack>
                            </Banner>
                        </Layout.Section>
                    )}

                    {cycleCount.status === STATUS.IN_PROGRESS && (
                        <>
                            <Layout.Section>
                                <Card>
                                    <BlockStack gap="400">
                                        <Text as="h2" variant="headingLg">
                                            Item Entry
                                        </Text>

                                        <EntryModeSelector entryMode={entryMode} onModeChange={handleMode} />

                                        {entryMode === ENTRY_MODES.SCAN && <ScanMode />}

                                        {entryMode === ENTRY_MODES.MANUAL && (
                                            <ManualEntryForm
                                                skuInput={skuInput}
                                                setSkuInput={setSkuInput}
                                                barcodeInput={barcodeInput}
                                                setBarcodeInput={setBarcodeInput}
                                                quantityInput={quantityInput}
                                                setQuantityInput={setQuantityInput}
                                                onSubmit={handleManualSubmit}
                                            />
                                        )}

                                        {entryMode === ENTRY_MODES.CSV && (
                                            <CsvUploadForm
                                                currentBatch={currentBatch}
                                                cycleCountId={id}
                                                onUploadSuccess={handleCsvUploadSuccess}
                                                addToast={addToast}
                                                isUploading={isCsvUploading}
                                                setIsUploading={setIsCsvUploading}
                                            />
                                        )}
                                    </BlockStack>
                                </Card>
                            </Layout.Section>

                            <Layout.Section>
                                <Card>
                                    <BatchManager
                                        batches={batches}
                                        currentBatch={currentBatch}
                                        showBatchCreate={showBatchCreate}
                                        newBatchDescription={newBatchDescription}
                                        editingBatchId={editingBatchId}
                                        editBatchDescription={editBatchDescription}
                                        onToggleCreate={() => setShowBatchCreate(!showBatchCreate)}
                                        onCreateBatch={handleCreateBatch}
                                        onBatchSelect={handleBatchSelect}
                                        onStartEditBatch={(batch) => {
                                            setEditingBatchId(batch.id);
                                            setEditBatchDescription(batch.description || '');
                                        }}
                                        onCancelEditBatch={() => {
                                            setEditingBatchId(null);
                                            setEditBatchDescription('');
                                        }}
                                        onUpdateBatchDescription={handleUpdateBatchDescription}
                                        setNewBatchDescription={setNewBatchDescription}
                                        setEditBatchDescription={setEditBatchDescription}
                                    />

                                    <div className={styles.countItemsContainer}>
                                        <BlockStack gap="400">
                                            <Text as="h2" variant="headingLg">
                                                Counted Items
                                            </Text>

                                            <CountedItemsTable
                                                countedItems={countedItems}
                                                editingItemSku={editingItemSku}
                                                editingQty={editingQty}
                                                onEditClick={handleEditClick}
                                                onQtyChange={handleQtyChange}
                                                onSaveEdit={handleSaveEdit}
                                                onCancelEdit={handleCancelEdit}
                                                onDeleteSku={handleDeleteSku}
                                                breakpoints={breakpoints}
                                                lastAddedSku={lastAddedSku}
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                onPageChange={handlePageChange}
                                                isLoadingItems={isLoadingItems}
                                                queryValue={countedItemsQuery}
                                                onQueryChange={handleCountedItemsQueryChange}
                                                onQueryClear={handleCountedItemsQueryClear}
                                                sortSelected={countedItemsSortSelected}
                                                onSortChange={handleCountedItemsSortChange}
                                                exactSkuFilter={countedItemsSkuFilter}
                                                onExactSkuFilterChange={handleCountedItemsSkuFilterChange}
                                                onClearSkuFilter={handleCountedItemsSkuFilterClear}
                                                quantityMinFilter={countedItemsQuantityMin}
                                                quantityMaxFilter={countedItemsQuantityMax}
                                                onQuantityMinFilterChange={handleCountedItemsQuantityMinChange}
                                                onQuantityMaxFilterChange={handleCountedItemsQuantityMaxChange}
                                                onClearQuantityRange={handleCountedItemsQuantityRangeClear}
                                            />
                                        </BlockStack>
                                    </div>
                                </Card>
                            </Layout.Section>

                            <Layout.Section>
                                <ExpectedItemsCard cycleCountId={id} breakpoints={breakpoints} />
                            </Layout.Section>
                        </>
                    )}
                </Layout>

                <ErrorModal active={showErrorModal} onClose={() => setShowErrorModal(false)} errors={statusErrors} />
            </Page>
        </>
    );
};

export default CycleCountPerform;
